import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { animate, useMotionValue, type PanInfo } from 'framer-motion';

import dayjs from 'shared/helpers/dayjs';
import { usePosthogCapture } from 'events/posthogCapture/_index';
import { Arrow } from 'icons/untitledui';
import * as Styled from 'events/styles/WeekWidget.style';

const SWIPE_THRESHOLD_PX = 50;
const VELOCITY_THRESHOLD_PX_PER_SEC = 250;
const WHEEL_IDLE_MS = 80;
const SNAP_TRANSITION = {
  type: 'spring' as const,
  stiffness: 180,
  damping: 26,
  mass: 1,
};

const DAY_LABEL_KEYS = [
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
  'sun',
] as const;

export interface WeekWidgetProps {
  selectedDate?: Date;
  onSelectDate?: (date: Date) => void;
}

export default function WeekWidget({
  selectedDate: selectedDateProp,
  onSelectDate,
}: WeekWidgetProps): JSX.Element {
  const { t } = useTranslation('pageEvents');
  const capture = usePosthogCapture();

  const today = useMemo(() => dayjs().startOf('day'), []);
  const currentWeekStart = useMemo(() => today.startOf('isoWeek'), [today]);

  const initialDate = selectedDateProp
    ? dayjs(selectedDateProp).startOf('day')
    : today;

  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [weekStart, setWeekStart] = useState(initialDate.startOf('isoWeek'));
  const [containerWidth, setContainerWidth] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const isAtCurrentWeek = weekStart.isSame(currentWeekStart, 'day');
  const isAtCurrentWeekRef = useRef(isAtCurrentWeek);
  isAtCurrentWeekRef.current = isAtCurrentWeek;

  const weeks = useMemo(
    () => [weekStart.subtract(1, 'week'), weekStart, weekStart.add(1, 'week')],
    [weekStart]
  );

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    setContainerWidth(el.offsetWidth);

    const observer = new ResizeObserver(() => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (containerWidth > 0) x.set(-containerWidth);
  }, [containerWidth, weekStart, x]);

  const decideDirection = (offsetX: number, velocityX: number): -1 | 0 | 1 => {
    const wantsPrev =
      offsetX > SWIPE_THRESHOLD_PX || velocityX > VELOCITY_THRESHOLD_PX_PER_SEC;
    const wantsNext =
      offsetX < -SWIPE_THRESHOLD_PX ||
      velocityX < -VELOCITY_THRESHOLD_PX_PER_SEC;
    if (wantsPrev) return -1;
    if (wantsNext && !isAtCurrentWeekRef.current) return 1;
    return 0;
  };

  const snapTo = (direction: -1 | 0 | 1) => {
    const target = -containerWidth - direction * containerWidth;
    animate(x, target, {
      ...SNAP_TRANSITION,
      onComplete: () => {
        if (direction === -1) {
          setWeekStart((prev) => prev.subtract(1, 'week'));
          capture.weekWidgetWeekChanged({ direction: 'previous' });
        } else if (direction === 1) {
          setWeekStart((prev) => prev.add(1, 'week'));
          capture.weekWidgetWeekChanged({ direction: 'next' });
        }
      },
    });
  };

  const handleDragEnd = (
    _: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo
  ) => {
    snapTo(decideDirection(info.offset.x, info.velocity.x));
  };

  const handlePrevWeek = () => snapTo(-1);
  const handleNextWeek = () => {
    if (isAtCurrentWeek) return;
    snapTo(1);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container || containerWidth === 0) return undefined;

    let wheelTimeout: ReturnType<typeof setTimeout> | null = null;
    let wheelAccum = 0;

    const finishWheel = () => {
      const direction = decideDirection(wheelAccum, 0);
      wheelAccum = 0;
      snapTo(direction);
    };

    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      event.preventDefault();

      wheelAccum -= event.deltaX;
      const minX = isAtCurrentWeekRef.current
        ? -containerWidth
        : -2 * containerWidth;
      const next = Math.max(minX, Math.min(0, -containerWidth + wheelAccum));
      x.set(next);
      wheelAccum = next + containerWidth;

      if (wheelTimeout !== null) clearTimeout(wheelTimeout);
      wheelTimeout = setTimeout(finishWheel, WHEEL_IDLE_MS);
    };

    container.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', onWheel);
      if (wheelTimeout !== null) clearTimeout(wheelTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerWidth]);

  const handleSelectDay = (day: dayjs.Dayjs) => {
    if (day.isAfter(today, 'day')) return;
    setSelectedDate(day);
    capture.weekWidgetDaySelected({
      days_from_today: day.diff(today, 'day'),
    });
    onSelectDate?.(day.toDate());
  };

  const dragConstraints = useMemo(
    () => ({
      left: isAtCurrentWeek ? -containerWidth : -2 * containerWidth,
      right: 0,
    }),
    [isAtCurrentWeek, containerWidth]
  );

  return (
    <Styled.Wrapper
      data-testid="week-widget"
      role="group"
      aria-label={t('weekWidget.ariaLabel')}
    >
      <Styled.ArrowButton
        type="button"
        onClick={handlePrevWeek}
        aria-label={t('weekWidget.previousWeek')}
      >
        <Arrow.ChevronLeft />
      </Styled.ArrowButton>
      <Styled.Container ref={containerRef}>
        <Styled.Track
          style={{ x }}
          drag="x"
          dragConstraints={dragConstraints}
          dragElastic={0}
          dragMomentum={false}
          onDragEnd={handleDragEnd}
        >
          {weeks.map((wkStart, weekIndex) => {
            const isVisibleWeek = weekIndex === 1;
            const weekDays = Array.from({ length: 7 }, (_, i) =>
              wkStart.add(i, 'day')
            );
            return (
              <Styled.Week
                key={wkStart.format('YYYY-MM-DD')}
                aria-hidden={!isVisibleWeek}
              >
                {weekDays.map((day, dayIndex) => {
                  const isSelected = day.isSame(selectedDate, 'day');
                  const isFuture = day.isAfter(today, 'day');
                  const isFirstOfMonth = day.date() === 1;
                  const labelKey = DAY_LABEL_KEYS[dayIndex];
                  const label = isFirstOfMonth
                    ? day.format('MMM')
                    : t(`weekWidget.${labelKey}`);

                  return (
                    <Styled.DayCell
                      key={day.format('YYYY-MM-DD')}
                      type="button"
                      $selected={isSelected}
                      $faded={isFuture}
                      disabled={isFuture || !isVisibleWeek}
                      tabIndex={isVisibleWeek ? 0 : -1}
                      aria-pressed={isSelected}
                      aria-label={day.format('dddd, MMMM D')}
                      onClick={() => handleSelectDay(day)}
                    >
                      <Styled.DayLabel
                        $selected={isSelected}
                        $faded={isFuture}
                        $isMonth={isFirstOfMonth}
                      >
                        {label}
                      </Styled.DayLabel>
                      <Styled.DayNumber>{day.date()}</Styled.DayNumber>
                    </Styled.DayCell>
                  );
                })}
              </Styled.Week>
            );
          })}
        </Styled.Track>
      </Styled.Container>
      <Styled.ArrowButton
        type="button"
        onClick={handleNextWeek}
        disabled={isAtCurrentWeek}
        aria-label={t('weekWidget.nextWeek')}
      >
        <Arrow.ChevronRight />
      </Styled.ArrowButton>
    </Styled.Wrapper>
  );
}
